import { Component, DOCUMENT, Inject, inject } from "@angular/core";
import { EventService, overlayType } from "./core/services/event.service";
import { BehaviorSubject, distinctUntilChanged, filter } from "rxjs";
import { LanguageService } from "./shared/services/language/language.service";
import { ThemeService } from "./core/services/theme.service";
import { UpdateService } from "@assoanalytics/app/core/services/update.service";
import { AuthService } from "./core/services/auth.service";
import { AclService } from "./core/acl/acl.service";
import { StorageService } from "./core/services/storage.service";
import { ResponsiveService } from "./core/services/responsive.service";
import { AclType } from "./core/models/user-login";
import { aclConfig } from "./acl.config";
import { FilterInputService } from "./shared/services/filter-input/filter-input.service";
import { NavigationStart, Router } from "@angular/router";
import { BreadcrumbService } from "./shared/services/breadcrumb/breadcrumb.service";
import { ArchivesService } from "./core/services/archives.service";
import { User } from "./core/models/user/user";
import { defaults } from "./app.config";
import { Title } from "@angular/platform-browser";
import { NGXLogger } from "ngx-logger";
import { PlantsService } from "./core/services/plants.service";
import { AlertDialogService } from "./shared/services/alert-dialog/alert-dialog.service";
import { NavService } from "./core/services/nav.service";
import { PreviousRouteService } from "./shared/services/previous-route.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false,
})
export class AppComponent {
  title = defaults.appName;
  loadingAction = new BehaviorSubject<overlayType>({
    loading: false,
    loaderBar: true,
  });
  private titleService = inject(Title); // Iniezione del servizio Title
  private bonusModalShown = false;
  showCookieBanner = false;
  privacyUrl = defaults.privacyLink;
  private firstAccessHandled = false;
  private lastIsFirstAccess = false;
  private lastIsPasswordExpired = false;

  constructor(
    private logger: NGXLogger,
    private plantService: PlantsService,
    private breadcrumbService: BreadcrumbService,
    private router: Router,
    private archivesService: ArchivesService,
    private authService: AuthService,
    private storageService: StorageService,
    private languageService: LanguageService,
    private aclService: AclService,
    private alertDialogService: AlertDialogService,
    private updateService: UpdateService,
    private responsiveService: ResponsiveService,
    private themeService: ThemeService,
    private navService: NavService,
    @Inject(DOCUMENT) private document: Document,
    private previousRouteService: PreviousRouteService, // anche se non usato in app.component serve per inizializzarlo e collezionare le rotte
  ) {
    // Inizializza il servizio di aggiornamento per il controllo delle nuove versioni
    this.updateService.init();

    EventService.overlayShow.subscribe((value: overlayType) => {
      this.loadingAction.next({ loaderBar: true, ...value });
    });

    this.setTitle();

    this.themeService.init();

    this.checkCookieConsent();
  }

  ngOnInit(): void {
    this.languageService.initTranslateService();

    // Mantieni aggiornato il valore di isFirstAccess
    this.navService.isFirstAccess.subscribe((isFirstAccess: boolean) => {
      this.lastIsFirstAccess = isFirstAccess;
    });

    this.navService.isPasswordExpired.subscribe(
      (isPasswordExpired: boolean) => {
        this.lastIsPasswordExpired = isPasswordExpired;
      },
    );

    this.authService.currentUser.pipe(
      distinctUntilChanged((a, b) => a?.role === b?.role && a?.id === b?.id),
    ).subscribe({
      next: (value: User | null) => {
        if (value) {
          this.languageService.changeLanguageForRole(value.role);

          const acl = this.storageService.getAcl();
          if (acl) {
            this.aclService.setAclConfig(aclConfig, value.role, acl as AclType);
          } else {
            this.aclService.setAclConfig(aclConfig, value.role);
          }

          // FIX per successivi login con livello utente diverso
          this.responsiveService.setTemplates();
        }
      },
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(async (event: NavigationStart) => {
        this.breadcrumbService.customizeTitleEvent.emit(null);
        FilterInputService.showPageFilters.next(false);

        // Forza redirect a cambio password se necessario
        if (
          (this.lastIsFirstAccess || this.lastIsPasswordExpired) &&
          event.url !== "/anagrafica/modifica-password" &&
          event.url !== "/login"
        ) {
          await this.router.navigate(["/anagrafica/modifica-password"]);
        }
      });
  }

  checkCookieConsent() {
    const consent = this.getCookie("asso_cookie_consent");
    this.showCookieBanner = !consent;
  }

  acceptCookies() {
    this.setCookie("asso_cookie_consent", "true", 365); // 12 mesi
    this.showCookieBanner = false;
  }

  setCookie(name: string, value: string, days: number) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    this.document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      "; expires=" +
      expires +
      "; path=/; SameSite=Lax";
  }

  getCookie(name: string) {
    return this.document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="))
      ?.split("=")[1];
  }

  private setTitle(): void {
    const appName = defaults.appName || "ASSO Payments"; // Legge il nome dall'app config
    this.titleService.setTitle(appName);
  }
}
